import React, {
  useState,
  useContext,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import {
  Button,
  TextField,
  Checkbox,
  Grid,
  IconButton,
  Collapse,
  Alert,
  Box,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
  Fade,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Delete,
  Folder,
  FolderOpen,
  InsertDriveFile,
  CloudUpload,
  DragIndicator,
  Close,
  Edit,
  Check,
} from "@mui/icons-material";
import { encodeS3Path } from "../../../../Components/helpers/Helpers";
import {
  DocsContext,
  PermissionContext,
} from "../../../../Components/Context/Context";
import api from "../../../../api/axios";
import LoadingPage from "../../../../Components/Common/Loading/LoadingPage";
import { normalizeFolderDisplayName } from "../docsFolderDisplay";
import { DndContext, pointerWithin } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDroppable } from "@dnd-kit/core";
import { DragOverlay } from "@dnd-kit/core";

const EmptyFolderDrop = ({ folderKey, children }) => {
  const { isOver, setNodeRef } = useDroppable({ id: `${folderKey}::empty` });
  return (
    <Paper
      ref={setNodeRef}
      sx={{
        p: 2,
        // mt: 1,
        textAlign: "center",
        color: "#aaa",
        border: "1px dashed #1a73e8",
        // minHeight: "40px",
        height: "100%",
        borderBottomLeftRadius: "0px",
        borderTopLeftRadius: "0px",
        backgroundColor: isOver ? "#e3f2fd" : "#fff",
        boxShadow: "none",
      }}
    >
      {children}
    </Paper>
  );
};

const ArchivosMostrar = ({
  caseId,
  userLogged,
  setDocsChangedFlag,
  docsChangedFlag,
  selectedDocs,
  setSelectedDocs,
  dataCase,
  caseTasks,
  onDocsRendered,
}) => {
  const Swal = require("sweetalert2");
  const { can } = useContext(PermissionContext);
  const [fileNames, setFileNames] = useState({});
  const {
    docsInfo,
    docsInfoTransfered,
    docFlagTransfered,
    docsFlag,
    docsLoading,
    setDocsLoading,
  } = useContext(DocsContext);

  const [orderedDocs, setOrderedDocs] = useState({});
  const [openFolders, setOpenFolders] = useState({});
  const [allowCrossFolder, setAllowCrossFolder] = useState(true); // <- NUEVO: switch

  const [activeItem, setActiveItem] = useState(null);

  // ===============================
  // AGRUPAR DOCUMENTOS
  // ===============================
  const groupedDocsInfo = useMemo(() => {
    const companyName = dataCase?.company_relation?.company_name;
    const grouped = {};
    Object.entries(docsInfo || {}).forEach(([caseId, folders]) => {
      Object.entries(folders).forEach(([folderName, files]) => {
        files.forEach((file) => {
          if (!grouped[companyName]) grouped[companyName] = {};
          if (!grouped[companyName][caseId]) grouped[companyName][caseId] = {};
          if (!grouped[companyName][caseId][folderName])
            grouped[companyName][caseId][folderName] = [];

          grouped[companyName][caseId][folderName].push(file);
        });
      });
    });
    return grouped;
  }, [docsInfo]);

  const groupedDocsInfoTransfered = useMemo(() => {
    const grouped = {};
    Object.entries(docsInfoTransfered || {}).forEach(([caseId, folders]) => {
      Object.entries(folders).forEach(([folderName, files]) => {
        files.forEach((file) => {
          const companyName =
            file?.company_relation?.company_name ||
            file?.unmatched_doc?.case_relation?.company_relation
              ?.company_name ||
            file?.matched_doc?.case_relation?.company_relation?.company_name ||
            "Sin nombre asignado";

          if (!grouped[companyName]) grouped[companyName] = {};
          if (!grouped[companyName][caseId]) grouped[companyName][caseId] = {};
          if (!grouped[companyName][caseId][folderName])
            grouped[companyName][caseId][folderName] = [];

          grouped[companyName][caseId][folderName].push(file);
        });
      });
    });
    return grouped;
  }, [docsInfoTransfered]);

  const hasMainDocs = Object.keys(groupedDocsInfo || {}).length > 0;
  const hasTransferedDocs =
    Object.keys(groupedDocsInfoTransfered || {}).length > 0;
  const hasAnyDocs = hasMainDocs || hasTransferedDocs;

  const toggleFolder = (folderKey) => {
    setOpenFolders((prev) => ({ ...prev, [folderKey]: !prev[folderKey] }));
  };
  // ===============================
  // RENOMBRAR DOCUMENTO
  // ===============================
  const handleNewDocumentName = async (
    fileNewTitle,
    fileOldName,
    fullpath,
    inputValue,
  ) => {
    // const inputValue = fileNames[fileNewTitle];
    if (!inputValue) {
      return Swal.fire({
        title: "No se ha detectado el nombre del archivo",
        icon: "warning",
        confirmButtonColor: "#1ab394",
        confirmButtonText: "Continuar",
      });
    }
    const result = await Swal.fire({
      title: "¿Desea guardar el nombre del documento?",
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      confirmButtonText: "Sí",
      cancelButtonText: "Cancelar",
    });
    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Guardando...",
      text: "Por favor espera un momento",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const formData = new FormData();
      formData.append("case_id", caseId);
      formData.append("doc_title", inputValue);
      formData.append("doc_file", fileNewTitle);
      formData.append("doc_status", 1);
      formData.append("user_id", userLogged?.id);

      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/docs/newdoctitle`,
        formData,
      );
      Swal.close();

      if (data?.error?.errorInfo?.length > 0) {
        Swal.fire({
          title: "Error",
          text: "¡Ha ocurrido un error al subir el nombre del documento!",
          icon: "error",
          confirmButtonColor: "#1ab394",
          confirmButtonText: "Continuar",
        });
      } else {
        Swal.fire({
          title: "¡Guardado correctamente!",
          icon: "success",
          confirmButtonColor: "#1ab394",
          confirmButtonText: "Continuar",
        });
        setDocsLoading(true);
        setDocsChangedFlag((prev) => !prev);
      }
    } catch (error) {
      Swal.close();
      Swal.fire({
        title: "Error",
        text: "No se pudo completar la solicitud.",
        icon: "error",
        confirmButtonColor: "#1ab394",
        confirmButtonText: "Continuar",
      });
      console.error(error);
    }
  };

  const handleUpdateDocumentName = async (file, newTitle, setEditing) => {
    if (!newTitle) {
      return Swal.fire({
        title: "No se ha detectado el nombre del archivo",
        icon: "warning",
        confirmButtonColor: "#1ab394",
        confirmButtonText: "Continuar",
      });
    }

    const result = await Swal.fire({
      title: "¿Desea guardar el nombre del documento?",
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      confirmButtonText: "Sí",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Guardando...",
      text: "Por favor espera un momento",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/docs/updatedoctitle`,
        {
          id: file.doc_id,
          doc_title: newTitle,
        },
      );

      Swal.close();

      if (response?.data?.error) {
        Swal.fire({
          title: "Error",
          text: "¡Ha ocurrido un error al actualizar el nombre!",
          icon: "error",
        });
      } else {
        Swal.fire({
          title: "¡Guardado correctamente!",
          icon: "success",
        });

        // Actualizar el estado local de orderedDocs para reflejar el nuevo título
        setOrderedDocs((prev) => {
          const newOrdered = { ...prev };

          Object.keys(newOrdered).forEach((folderKey) => {
            newOrdered[folderKey] = newOrdered[folderKey].map((f) =>
              f.doc_id === file.doc_id ? { ...f, doc_title: newTitle } : f,
            );
          });

          return newOrdered;
        });

        // Cerrar el modo edición
        setEditing(false);

        // Opcional: seguir usando el flag para otros efectos
        setDocsLoading(true);
        setDocsChangedFlag((prev) => !prev);
      }
    } catch (error) {
      Swal.close();
      console.error(error);
    }
  };

  // ===============================
  // GUARDAR ORDEN
  // ===============================
  const handleSaveOrder = async () => {
    const result = await Swal.fire({
      title: "¿Deseas guardar el nuevo orden?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, guardar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Guardando orden...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const payload = [];

      Object.entries(orderedDocs).forEach(([folderKey, files]) => {
        const namedFiles = files;

        namedFiles.forEach((file, index) => {
          if (!file.transfered) {
            const cleanFolderName = folderKey
              .replace("-transfered", "")
              .split("-")
              .slice(1)
              .join("-"); // por si el folder tiene guiones

            payload.push({
              case_id: caseId, //  importante (real del archivo)
              path: file.path,
              order_index: index,
              folder: cleanFolderName,
              original_name: file.file_name,
              id: file.doc_id,
            });
          }
        });
      });

      //   "case_id: " + caseId + " docs: " + JSON.stringify(payload, null, 2),
      // );

      const formData = new FormData();
      formData.append("case_id", caseId);
      formData.append("docs", JSON.stringify(payload));

      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/docs/update-order`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
        },
      );

      Swal.close();
      Swal.fire({
        title: "Orden guardado correctamente",
        icon: "success",
        confirmButtonColor: "#1ab394",
      });
      setDocsLoading(true);
      setDocsChangedFlag((prev) => !prev);
    } catch (error) {
      console.error("ERROR AXIOS:", error);

      if (error.response) {
        console.error("STATUS:", error.response.status);
        console.error("DATA:", error.response.data);
        console.error("HEADERS:", error.response.headers);
      } else if (error.request) {
        console.error("NO RESPONSE:", error.request);
      } else {
        console.error("REQUEST ERROR:", error.message);
      }
    }
  };

  // ===============================
  // DRAG END
  // ===============================
  const handleDragEndGlobal = (event) => {
    const { active, over } = event;
    if (!over) return;

    const parseId = (id) => {
      const parts = id.split("::");
      return {
        folder: parts[0],
        path: parts[1] || null,
      };
    };

    const { folder: fromFolder, path: fromPath } = parseId(active.id);

    const file = orderedDocs[fromFolder]?.find((f) => f.path === fromPath);

    // Bloquear transferidos
    if (file?.transfered) return;

    const { folder: toFolder, path: toPath } = parseId(over.id);

    //  NO permitir soltar en carpetas de transferidos
    if (toFolder.includes("-transfered")) return;
    // Si no se permite mover entre carpetas
    if (!allowCrossFolder && fromFolder !== toFolder) return;

    setOrderedDocs((prev) => {
      const source = prev[fromFolder] || [];
      const target = prev[toFolder] || [];

      const draggedItem = source.find((i) => i.path === fromPath);
      if (!draggedItem) return prev;

      let newPrev = { ...prev };

      // Mover dentro de la misma carpeta
      if (fromFolder === toFolder) {
        const oldIndex = source.findIndex((i) => i.path === fromPath);
        const newIndex = source.findIndex((i) => i.path === toPath);
        if (oldIndex === -1 || newIndex === -1) return prev;

        newPrev[fromFolder] = arrayMove(source, oldIndex, newIndex);

        // Conservar carpeta aunque quede vacía
        if (!newPrev[fromFolder]) newPrev[fromFolder] = [];
        return newPrev;
      }

      // Mover entre carpetas diferentes
      const draggedItemWithMoved = {
        ...draggedItem,
        movedFrom: fromFolder,
      };

      const newSource = source.filter((i) => i.path !== fromPath);
      let newTarget = [...target];

      // Si la carpeta destino estaba vacía
      if (toPath === "empty") {
        newTarget.push(draggedItemWithMoved);
      } else {
        const overIndex = newTarget.findIndex((i) => i.path === toPath);
        if (overIndex === -1) newTarget.push(draggedItemWithMoved);
        else newTarget.splice(overIndex, 0, draggedItemWithMoved);
      }

      newPrev[toFolder] = newTarget;

      // Conservar carpeta origen aunque quede vacía
      if (newSource.length > 0) newPrev[fromFolder] = newSource;
      else newPrev[fromFolder] = [];

      return newPrev;
    });
  };

  // ===============================
  // ELIMINAR DOCUMENTO
  // ===============================
  const handleDelete = async (file) => {
    const formData = new FormData();
    formData.append("case_id", caseId);
    formData.append("full_path", file?.path);
    formData.append("doc_id", file?.doc_id || 0);

    const response = await Swal.fire({
      title: "¿Seguro que desea eliminar el archivo?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!response.isConfirmed) return;

    Swal.fire({
      title: "Eliminando archivos...",
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading(),
    });

    try {
      await api.post(`${process.env.REACT_APP_API_URL}/s3/destroy`, formData);
      Swal.close();
      Swal.fire({
        title: "¡Eliminado!",
        icon: "success",
        text: "La documentación se ha eliminado exitosamente",
        confirmButtonColor: "#1ab394",
      });
      setDocsLoading(true);
      setDocsChangedFlag((prev) => !prev);
    } catch (error) {
      Swal.close();
      Swal.fire({
        title: "Error",
        text: `Error al eliminar: ${error.message}`,
        icon: "error",
        confirmButtonColor: "#1ab394",
      });
      console.error(error);
    }
  };

  // ===============================
  // SELECCIÓN DE DOCUMENTOS
  // ===============================
  const handleSelectFolder = (folderKey, files, checked) => {
    setSelectedDocs((prev) => {
      const updated = { ...prev };
      updated[folderKey] = checked
        ? files.map((f) => ({ doc_id: f.doc_id, path: f.path }))
        : [];
      return updated;
    });
    if (checked && !openFolders[folderKey])
      setOpenFolders((prev) => ({ ...prev, [folderKey]: true }));
  };

  const handleSelectDoc = (folderKey, file, checked) => {
    setSelectedDocs((prev) => {
      const updated = { ...prev };
      const docs = updated[folderKey] ? [...updated[folderKey]] : [];
      if (checked) {
        if (!docs.some((d) => d.doc_id === file.doc_id && d.path === file.path))
          docs.push({ doc_id: file.doc_id, path: file.path });
      } else {
        updated[folderKey] = docs.filter(
          (d) => !(d.doc_id === file.doc_id && d.path === file.path),
        );
        return updated;
      }
      updated[folderKey] = docs;
      return updated;
    });
  };

  // ===============================
  // ITEM ORDENABLE
  // ===============================
  const SortableItem = ({ id, children, transfered = false }) => {
    const {
      setNodeRef,
      transform,
      transition,
      attributes,
      listeners,
      isDragging,
    } = useSortable({
      id,
      disabled: transfered, // evita drag pero respeta hooks
      transition: {
        duration: 350,
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
      },
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      display: "flex",
      width: "100%",
      alignItems: "center",
      marginTop: "4px",
      cursor: transfered ? "default" : isDragging ? "grabbing" : "default",
    };

    return (
      <div ref={setNodeRef} style={style}>
        {!transfered && (
          <Tooltip
            title="Arrastra para reordenar o mover entre carpetas"
            enterDelay={300}
            arrow
            placement="left"
          >
            <Button
              {...attributes}
              {...listeners}
              disableRipple
              variant="outlined"
              sx={{
                cursor: isDragging ? "grabbing" : "grab",
                minWidth: "40px",
                height: "-webkit-fill-available",
                borderTopRightRadius: "0px",
                borderBottomRightRadius: "0px",
              }}
            >
              <DragIndicator />
            </Button>
          </Tooltip>
        )}

        <div
          style={{
            flex: 1,
            minWidth: 0,
          }}
        >
          {children}
        </div>
      </div>
    );
  };

  // ===============================
  // FILE ITEM
  // ===============================
  const extractFeedEvents = (html = "") => {
    if (!html) return [];

    const container = document.createElement("div");
    container.innerHTML = html;

    const elements = container.querySelectorAll(".feed-element");

    return Array.from(elements).map((el) => {
      const rawText = el.innerText?.replace(/\s+/g, " ").trim();

      const labelDate = el.querySelector(".label")?.innerText?.trim();

      const strongText = el.querySelector("strong")?.innerText?.trim() || "";

      const boldText = el.querySelector("b")?.innerText?.trim() || "";

      //  CASO 1: "Notificación recibida el XX/XX"
      let type = "";
      let date = labelDate || "";

      const matchInline = strongText.match(/^(.*)\s+el\s+(.+)$/i);

      if (matchInline) {
        type = matchInline[1].trim();
        date = matchInline[2].trim();
      } else {
        type = strongText.replace(":", "").trim() || "EVENTO";
      }

      //  CASO 2: NOTA con contenido
      const items = boldText ? boldText.split(",").map((s) => s.trim()) : [];

      return {
        type, // izquierda UI
        date, // derecha UI
        detail: rawText,
        items,
      };
    });
  };

  const normalizeDate = (value) => {
    if (!value) return null;

    const d = new Date(
      typeof value === "string" ? value.replace(" ", "T") : value,
    );

    if (isNaN(d.getTime())) return null;

    // normalizamos a YYYY-MM-DD HH:mm (minuto exacto)
    return d.toISOString().slice(0, 16);
  };

  const normalizeText = (value = "") => {
    return String(value)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]/g, "")
      .trim();
  };

  const extractDateFromText = (value = "") => {
    const text = String(value || "");
    const match = text.match(/\b\d{2}\/\d{2}\/\d{4}(?:\s+\d{2}:\d{2})?\b/);
    return match ? match[0] : "";
  };

  const formatDisplayDate = (value) => {
    if (!value) return "Sin fecha";

    if (typeof value === "string") {
      const extracted = extractDateFromText(value);
      if (extracted) return extracted;
    }

    const d = new Date(
      typeof value === "string" ? value.replace(" ", "T") : value,
    );
    if (isNaN(d.getTime())) return "Sin fecha";

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");

    const hasTime =
      typeof value === "string"
        ? /\d{2}:\d{2}/.test(value) || !/[T\s]00:00(:00)?/.test(value)
        : d.getHours() !== 0 || d.getMinutes() !== 0;

    return hasTime
      ? `${day}/${month}/${year} ${hours}:${minutes}`
      : `${day}/${month}/${year}`;
  };

  const basename = (value = "") => {
    const clean = String(value || "")
      .split("?")[0]
      .split("#")[0];
    try {
      return decodeURIComponent(clean).split("/").pop() || "";
    } catch {
      return clean.split("/").pop() || "";
    }
  };

  const basenameWithoutExt = (value = "") => {
    const name = basename(value);
    return name.replace(/\.[^.]+$/, "");
  };

  const resolveConfigNode = (cfg = {}, keys = []) => {
    const available = Object.keys(cfg || {});

    for (const key of keys) {
      const match = available.find(
        (candidate) => normalizeText(candidate) === normalizeText(key),
      );

      if (match && cfg?.[match]) {
        return cfg[match];
      }
    }

    return null;
  };

  const isSameFileByConfig = (file, configFileValue) => {
    if (!configFileValue) return false;

    const fileCandidates = [
      basename(file?.path),
      basename(file?.file_name),
      basename(file?.doc_title),
      basenameWithoutExt(file?.file_name),
      basenameWithoutExt(file?.doc_title),
    ]
      .filter(Boolean)
      .map((item) => normalizeText(item));

    const cfgCandidates = [
      basename(configFileValue),
      basenameWithoutExt(configFileValue),
      String(configFileValue || "")
        .split("/")
        .pop(),
    ]
      .filter(Boolean)
      .map((item) => normalizeText(item));

    return cfgCandidates.some(
      (cfgName) =>
        cfgName && fileCandidates.some((fileName) => fileName === cfgName),
    );
  };

  const getRevisionRequests = (tasks = []) => {
    const requests = [];

    tasks.forEach((task) => {
      const events = extractFeedEvents(task?.message || "");

      events.forEach((event) => {
        const eventTypeNormalized = normalizeText(event?.type || "");
        const isRevisionRequest =
          eventTypeNormalized.includes("nota") ||
          normalizeText(event?.detail || "").includes(
            "senecesitamasdocumentacion",
          );

        if (
          !isRevisionRequest ||
          !Array.isArray(event?.items) ||
          event.items.length === 0
        ) {
          return;
        }

        requests.push({
          items: event.items,
          date: event.date || "",
        });
      });
    });

    return requests;
  };

  const addAnnotation = (list, type, dateValue, source = "") => {
    const date = formatDisplayDate(dateValue);
    const key = `${type}-${date}-${source}`;

    // Evita duplicados por tipo (un archivo solo debe mostrar un chip por categoria)
    if (list.some((item) => item.type === type)) {
      return;
    }

    const visual = {
      solicitud: {
        label: `Solicitud enviada - ${date}`,
        color: "info",
        variant: "filled",
      },
      notificacion: {
        label: `Notificación enviada - ${date}`,
        color: "warning",
        variant: "filled",
      },
      revision: {
        label: `Revisión enviada - ${date}`,
        color: "secondary",
        variant: "outlined",
      },
    };

    list.push({
      key,
      type,
      ...visual[type],
    });
  };

  const getFileAnnotations = (file, tasks = []) => {
    const annotations = [];

    tasks.forEach((task) => {
      const cfg =
        task?.config && typeof task.config === "object" ? task.config : {};

      const solicitudCfg = resolveConfigNode(cfg, ["solicitud"]);
      const notificacionCfg = resolveConfigNode(cfg, [
        "notification",
        "notificacion",
      ]);

      if (solicitudCfg?.file && isSameFileByConfig(file, solicitudCfg.file)) {
        addAnnotation(
          annotations,
          "solicitud",
          solicitudCfg?.date || file?.date_in,
          "cfg-sol",
        );
      }

      if (
        notificacionCfg?.file &&
        isSameFileByConfig(file, notificacionCfg.file)
      ) {
        addAnnotation(
          annotations,
          "notificacion",
          notificacionCfg?.date || file?.date_in,
          "cfg-not",
        );
      }
    });

    const desc = String(file?.doc_desc || "");
    const descNorm = normalizeText(desc);
    const descDate = extractDateFromText(desc) || file?.date_in;

    if (descNorm.includes("presentado") || descNorm.includes("solicitud")) {
      addAnnotation(annotations, "solicitud", descDate, "desc-sol");
    }

    // Marcamos notificacion por texto explicito o por legado
    // (antes se guardaba como "Recibido el ..." sin la palabra notificacion).
    if (
      descNorm.includes("notificacion") ||
      (descNorm.startsWith("recibido") && !descNorm.includes("enviado"))
    ) {
      addAnnotation(annotations, "notificacion", descDate, "desc-not");
    }

    if (descNorm.includes("recibidoenviado")) {
      addAnnotation(annotations, "revision", descDate, "desc-rev");
    }

    const revisionRequests = getRevisionRequests(tasks);
    const fileNameComparable = normalizeText(
      basenameWithoutExt(file?.doc_title || file?.file_name || file?.path),
    );

    revisionRequests.forEach((request) => {
      const matchByName = request.items.some((item) => {
        const requested = normalizeText(item);
        return (
          requested &&
          fileNameComparable &&
          (fileNameComparable.includes(requested) ||
            requested.includes(fileNameComparable))
        );
      });

      if (matchByName) {
        addAnnotation(
          annotations,
          "revision",
          request?.date || file?.date_in,
          "msg-rev",
        );
      }
    });

    return annotations;
  };

  const extractTaskDates = (tasks = []) => {
    const messageDates = [];
    const configDates = [];

    tasks.forEach((task) => {
      const message = task?.message;
      const cfg = task?.config;

      // -----------------------------
      // 1. EXTRAER DEL MESSAGE (PRIORIDAD ALTA)
      // -----------------------------
      if (message) {
        const container = document.createElement("div");
        container.innerHTML = message;

        const elements = container.querySelectorAll(".feed-element");

        Array.from(elements).forEach((el) => {
          const strongText =
            el.querySelector("strong")?.innerText?.trim() || "";
          const labelDate = el.querySelector(".label")?.innerText?.trim() || "";

          const match = strongText.match(/^(.*)\s+el\s+(.+)$/i);

          let type = match
            ? match[1].trim()
            : strongText.replace(":", "").trim();
          let date = match ? match[2].trim() : labelDate;

          messageDates.push({
            type,
            date: normalizeDate(date),
          });
        });
      }

      // -----------------------------
      // 2. EXTRAER DEL CONFIG (FALLBACK)
      // -----------------------------
      if (cfg) {
        if (cfg.notification?.date) {
          configDates.push({
            type: "Notificación",
            date: normalizeDate(cfg.notification.date),
          });
        }

        if (cfg.solicitud?.date) {
          configDates.push({
            type: "Solicitud",
            date: normalizeDate(cfg.solicitud.date),
          });
        }
      }
    });

    // -----------------------------
    // 3. FILTRO INTELIGENTE
    // config solo entra si NO existe en message
    // -----------------------------
    const messageSet = new Set(messageDates.map((d) => d.date));
    const filteredConfig = configDates.filter((d) => !messageSet.has(d.date));

    return [...messageDates, ...filteredConfig];
  };

  const getFileFlags = (file, tasks = []) => {
    const flags = new Set();

    const fileId = normalizeFile(file?.file_name || file?.path);

    const fileDate = normalizeDate(file?.date_in);

    const taskDates = extractTaskDates(tasks);

    tasks?.forEach((task) => {
      const cfg = task?.config;
      if (!cfg) return;

      const check = (value, label) => {
        const normalized = normalizeTaskFile(value);

        if (normalized && normalized === fileId) {
          flags.add(label);
        }
      };

      check(cfg.morefiles, "Revisión");
      check(cfg.solicitud, "Solicitud");
      check(cfg.notification, "Notificación");
    });

    //  FALLBACK POR FECHA (NUEVO)
    if (flags.size === 0 && fileDate) {
      taskDates.forEach((t) => {
        if (!t.date) return;

        if (t.date === fileDate) {
          flags.add(t.type);
        }
      });
    }

    //  FALLBACK POR FECHA (NUEVO)
    // if (file?.doc_desc) {
    //   taskDates.forEach((t) => {

    //     flags.add(t?.type ? t?.type : "");
    //   });
    // }

    return [...flags];
  };

  const normalizeTaskFile = (value) => {
    if (!value) return "";

    if (typeof value === "string") {
      return normalizeFile(value);
    }

    if (typeof value === "object") {
      return normalizeFile(value.file || value.name || value.path);
    }

    return "";
  };

  const normalizeFile = (value) => {
    if (!value) return "";

    return value.split("/").pop().trim().toLowerCase();
  };

  const normalizeRaw = (str) => String(str).replace(/\s+/g, " ").trim();

  const parseDocDesc = (desc = "", flags = "") => {
    if (!desc) return null;

    // CASO 1
    const matchMain = desc.match(
      /^(Notificación|Solicitud|Recibido|Enviado|Presentado)\s*(?:el|desde)?\s*(.+)?$/i,
    );

    if (matchMain) {
      return {
        type: matchMain[0],
        // action: matchMain[2],
        // date: matchMain[matchMain?.length - 1],
        flag: flags,
      };
    }

    // CASO 2 (MUCHO MÁS FLEXIBLE)
    const matchTransfer = desc.match(
      /^(Recibido|Enviado)\s*(?:el|desde)?\s*(.+)$/i,
    );

    if (matchTransfer) {
      return {
        type: matchTransfer[1],
        date: matchMain[matchMain?.length - 1],
        flag: "Revisión",
      };
    }

    return null;
  };

  const FileItem = React.memo(
    (props) => {
      const {
        file,
        folderKey,
        transfered,
        selectedDocs,
        handleSelectDoc,
        handleDelete,
        handleNewDocumentName,
        handleUpdateDocumentName,
        can,
        caseTasks,
        docsChangedFlag,
        // fileNames,
      } = props;

      const [editing, setEditing] = useState(false);
      const [editValue, setEditValue] = useState(file.doc_title || "");

      // const identifiedDoc = extractDocsFromMessage(taskWithMoreFiles);

      const movedFromFolder = transfered
        ? file.movedFromFolder || "carpeta anterior"
        : null;

      const fileWithFlag = useMemo(() => {
        const flags = getFileFlags(file, caseTasks);
        const chips = getFileAnnotations(file, caseTasks);

        const parsed = parseDocDesc(file?.doc_desc, flags[0]);
        const feed = !parsed ? extractFeedEvents(file?.doc_desc) : null;

        const meta =
          parsed ||
          (feed?.length
            ? {
                type: feed[0].type,
                date: feed[0].date,
                items: feed[0].items,
                source: "feed",
                flag: feed[0].type,
              }
            : null);

        return {
          ...file,
          flags,
          meta,
          chips,
          type: meta?.flag || flags?.[0] || null,
          date: meta?.date || file?.date_in,
        };
      }, [file, caseTasks]);

      const [localName, setLocalName] = useState(
        fileNames[fileWithFlag?.path] ?? "",
      );

      useEffect(() => {
        setLocalName(fileNames[file?.path] ?? "");
      }, [fileNames, file?.path]);

      return (
        <Paper
          sx={{
            p: 0,
            backgroundColor: "#fff",
            transition: "all 0.25s ease",
            "&:hover": { backgroundColor: "#f0f0f0" },
            borderBottomLeftRadius: "0px",
            borderTopLeftRadius: "0px",
            boxShadow: "none",
            border: "1px solid rgba(0, 0, 0, 0.38)",
            borderLeft: "none",
          }}
        >
          {Array.isArray(fileWithFlag?.chips) &&
            fileWithFlag.chips.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  px: 1.5,
                  py: 0.8,
                  flexWrap: "wrap",
                  gap: 0.8,
                }}
              >
                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                  {fileWithFlag.chips.map((chip) => (
                    <Chip
                      key={chip.key}
                      size="small"
                      label={chip.label}
                      color={chip.color}
                      variant={chip.variant}
                      sx={{ fontWeight: 600 }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          <Grid
            container
            alignItems="center"
            wrap="nowrap"
            sx={{
              p: transfered ? "6px" : "",
            }}
          >
            {/* IZQUIERDA: checkbox + icono + nombre */}
            <Grid
              item
              sx={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                minWidth: 0,
                gap: 1,
              }}
            >
              {!transfered && (
                <Checkbox
                  checked={selectedDocs[folderKey]?.some(
                    (d) =>
                      d.doc_id === fileWithFlag?.doc_id &&
                      d.path === fileWithFlag?.path,
                  )}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectDoc(folderKey, fileWithFlag, e.target.checked);
                  }}
                />
              )}

              <InsertDriveFile
                color="info"
                sx={{
                  paddingLeft: transfered ? "10px" : "",
                }}
              />

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {editing ? (
                  !transfered && (
                    <TextField
                      size="small"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleUpdateDocumentName(
                            fileWithFlag,
                            editValue,
                            setEditing,
                          );
                        }
                      }}
                      sx={{
                        flex: 1,
                        "& input": {
                          fontSize: 13,
                          padding: "4px",
                        },
                      }}
                    />
                  )
                ) : (
                  <Tooltip
                    title={
                      fileWithFlag?.matched
                        ? fileWithFlag?.doc_title
                        : fileWithFlag?.file_name
                    }
                    arrow
                    placement="left"
                  >
                    <a
                      href={`${process.env.REACT_APP_S3_API_URL}/${encodeS3Path(fileWithFlag?.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: "none",
                        color: "#000",
                        fontWeight: fileWithFlag?.matched ? 700 : 400,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%",
                        display: "block",
                      }}
                    >
                      {fileWithFlag?.matched
                        ? fileWithFlag?.doc_title
                        : fileWithFlag?.file_name}
                    </a>
                  </Tooltip>
                )}
              </Box>
            </Grid>

            {/* DERECHA: botones */}
            <Grid
              item
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                ml: 1,
              }}
            >
              {!transfered && fileWithFlag?.matched && can("cases", "docs") && (
                <>
                  {editing && (
                    <IconButton
                      size="small"
                      sx={{ color: "#1a73e8" }}
                      onClick={() => {
                        handleUpdateDocumentName(
                          fileWithFlag,
                          editValue,
                          setEditing,
                        );
                      }}
                    >
                      <Check />
                    </IconButton>
                  )}

                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditing(!editing);
                    }}
                  >
                    {editing ? <Close /> : <Edit />}
                  </IconButton>
                </>
              )}

              {!transfered && can("cases", "deletedocs") && !editing && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(fileWithFlag)}
                >
                  <Delete />
                </IconButton>
              )}
            </Grid>
          </Grid>

          {/* {movedFromFolder && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Archivo movido desde: <strong>{movedFromFolder}</strong>
            </Alert>
          )} */}

          {!fileWithFlag?.matched && can("cases", "docs") && !transfered && (
            <Box sx={{ display: "flex", gap: "8px", padding: 1 }}>
              <TextField
                size="small"
                label="Nombrar archivo"
                autoComplete="new-password"
                value={localName}
                onChange={(e) => setLocalName(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": {
                    height: "34px !important",
                    paddingLeft: "10px !important",
                  },
                  "& .MuiInputBase-input": {
                    height: "34px !important",
                  },
                }}
              />
              <Button
                variant="outlined"
                onClick={() =>
                  handleNewDocumentName(
                    fileWithFlag?.path,
                    fileWithFlag?.file_name,
                    fileWithFlag?.path,
                    localName,
                  )
                }
              >
                <CloudUpload />
              </Button>
            </Box>
          )}
          {(fileWithFlag?.matched ||
            (Array.isArray(fileWithFlag?.chips) &&
              fileWithFlag.chips.length > 0)) && (
            <>
              <Divider
                sx={{
                  mt: 1,
                  borderColor: "rgba(0,0,0,0.08)",
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 0.8,
                  fontSize: "11.5px",
                  color: "#666",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "11.5px",
                    fontWeight: "600",
                    color: "#777",
                  }}
                >
                  Subido por: {fileWithFlag?.lawyer_name || "No disponible"}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "11.5px",
                    color: "#777",
                    fontWeight: "600",
                  }}
                >
                  {Array.isArray(fileWithFlag?.chips) &&
                  fileWithFlag.chips.length > 0
                    ? `Recibido: ${formatDisplayDate(fileWithFlag?.date_in)}`
                    : formatDisplayDate(fileWithFlag?.date_in)}
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      );
    },
    (prev, next) => {
      return (
        prev?.file.path === next?.file.path &&
        prev?.file.doc_id === next?.file.doc_id &&
        prev?.selectedDocs === next?.selectedDocs &&
        prev?.transfered === next?.transfered &&
        prev?.file.doc_title === next?.file.doc_title &&
        prev?.docsChangedFlag === next?.docsChangedFlag
      );
    },
  );
  // ===============================
  // RENDER DOCUMENTOS
  // ===============================
  const renderDocumentSection = useCallback(
    (docsData, sectionTitle, transfered = false, docFlag = true) => {
      if (!docFlag)
        return (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        );

      if (!docsData || Object.entries(docsData).length === 0) {
        return null;
      }

      const canDelete = can("cases", "deletedocs");

      return (
        <Box sx={{ width: "100%", mb: 4 }}>
          {sectionTitle && (
            <Typography variant="h6" sx={{ mb: 2 }}>
              {sectionTitle}
            </Typography>
          )}

          {Object.entries(docsData)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([companyName, cases]) => (
              <Box
                key={companyName}
                // sx={{ p: 2, mb: 2, backgroundColor: "#f9f9f9" }}
              >
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1 }}>
                  {companyName}
                </Typography>

                {Object.entries(cases).map(([caseId, folders]) => (
                  <Box key={caseId} sx={{ ml: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Expediente #{caseId}
                    </Typography>

                    {Object.entries(folders)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([folderName, files]) => {
                        const folderKey = `${caseId}-${folderName}${transfered ? "-transfered" : ""}`;
                        const filesInFolder = orderedDocs[folderKey] || [];
                        const isEmpty = filesInFolder.length === 0;
                        const isOpen = openFolders[folderKey] ?? false;

                        return (
                          <Paper
                            key={folderKey}
                            sx={{
                              mb: 1,
                              p: 1,
                              borderLeft: "4px solid #1a73e8",
                              cursor: "pointer",
                              backgroundColor: "#f9f9f9",
                            }}
                          >
                            <Grid
                              container
                              alignItems="center"
                              sx={{
                                width: "100%",
                              }}
                              onClick={() => toggleFolder(folderKey)}
                            >
                              <Grid item xs={transfered ? 0 : 1}>
                                {!transfered && (
                                  <Checkbox
                                    checked={
                                      (selectedDocs[folderKey]?.length || 0) ===
                                      filesInFolder.length
                                    }
                                    indeterminate={
                                      selectedDocs[folderKey]?.length > 0 &&
                                      selectedDocs[folderKey]?.length <
                                        filesInFolder.length
                                    }
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleSelectFolder(
                                        folderKey,
                                        filesInFolder,
                                        e.target.checked,
                                      );
                                    }}
                                    sx={{ mr: 1 }}
                                  />
                                )}
                              </Grid>
                              <Grid
                                item
                                xs={transfered ? 12 : 11}
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                {isOpen ? (
                                  <FolderOpen color="warning" sx={{ mr: 1 }} />
                                ) : (
                                  <Folder color="warning" sx={{ mr: 1 }} />
                                )}
                                <Typography variant="body1">
                                  {normalizeFolderDisplayName(folderName)}
                                </Typography>
                                {filesInFolder.some((f) => f.transfered) && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      ml: 1,
                                      display: "block",
                                      color: "#555",
                                    }}
                                  >
                                    -{" "}
                                    {
                                      filesInFolder.filter((f) => f.transfered)
                                        .length
                                    }{" "}
                                    Archivo(s) transferido(s)
                                  </Typography>
                                )}
                                {filesInFolder.length > 0 && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      ml: 1,
                                      display: "block",
                                      color: "#555",
                                    }}
                                  >
                                    {" - "}
                                    {filesInFolder.length} Archivo(s)
                                  </Typography>
                                )}
                              </Grid>
                            </Grid>

                            <Collapse in={isOpen} timeout={300} unmountOnExit>
                              <SortableContext
                                items={filesInFolder.map(
                                  (f) => `${folderKey}::${f.path}`,
                                )}
                                strategy={verticalListSortingStrategy}
                              >
                                {isEmpty ? (
                                  <SortableItem id={`${folderKey}::empty`}>
                                    <EmptyFolderDrop folderKey={folderKey}>
                                      Carpeta vacía. Arrastre archivos aquí.
                                    </EmptyFolderDrop>
                                  </SortableItem>
                                ) : (
                                  <>
                                    {(() => {
                                      const namedFiles = filesInFolder.filter(
                                        (f) => f.matched,
                                      );
                                      const unnamedFiles = filesInFolder.filter(
                                        (f) => !f.matched,
                                      );

                                      const fileItemProps = {
                                        folderKey,
                                        transfered,
                                        selectedDocs,
                                        handleSelectDoc,
                                        handleDelete,
                                        handleNewDocumentName,
                                        handleUpdateDocumentName,
                                        can,
                                        docsChangedFlag,
                                        caseTasks,
                                      };

                                      return (
                                        <>
                                          {unnamedFiles.length > 0 && (
                                            <>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  ml: 1,
                                                  mt: 1,
                                                  display: "block",
                                                  color: "#000000",
                                                }}
                                              >
                                                Archivos sin nombrar
                                              </Typography>

                                              {unnamedFiles.map((file) => (
                                                <SortableItem
                                                  transfered={transfered}
                                                  id={`${folderKey}::${file.path}`}
                                                  key={file.doc_id || file.path}
                                                >
                                                  <FileItem
                                                    {...fileItemProps}
                                                    file={file}
                                                  />
                                                </SortableItem>
                                              ))}
                                            </>
                                          )}

                                          {namedFiles.length > 0 && (
                                            <>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  ml: 1,
                                                  mt: 1,
                                                  display: "block",
                                                  color: "#000000",
                                                }}
                                              >
                                                Archivos nombrados
                                              </Typography>

                                              {namedFiles.map((file) => (
                                                <SortableItem
                                                  transfered={transfered}
                                                  id={`${folderKey}::${file.path}`}
                                                  key={file.doc_id || file.path}
                                                >
                                                  <FileItem
                                                    {...fileItemProps}
                                                    file={file}
                                                  />
                                                </SortableItem>
                                              ))}
                                            </>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </>
                                )}
                              </SortableContext>
                            </Collapse>
                          </Paper>
                        );
                      })}
                  </Box>
                ))}
              </Box>
            ))}
        </Box>
      );
    },
    [
      orderedDocs,
      openFolders,
      selectedDocs,
      can,
      docFlagTransfered,
      handleSelectFolder,
      handleSelectDoc,
      handleDelete,
      handleNewDocumentName,
      handleUpdateDocumentName,
      docsChangedFlag,
    ],
  );

  useEffect(() => {
    if (
      docsLoading &&
      (Object.keys(docsInfo || {}).length > 0 ||
        Object.keys(docsInfoTransfered || {}).length > 0)
    ) {
      const frameId = window.requestAnimationFrame(() => {
        if (typeof onDocsRendered === "function") {
          onDocsRendered();
        }

        if (typeof setDocsLoading === "function") {
          setDocsLoading(false);
        }
      });

      return () => window.cancelAnimationFrame(frameId);
    }
  }, [
    docsLoading,
    docsInfo,
    docsInfoTransfered,
    onDocsRendered,
    setDocsLoading,
  ]);

  useEffect(() => {
    setOrderedDocs((prev) => {
      //  Solo conservar estado si NO hubo cambios
      // if (!docsChangedFlag && Object.keys(prev).length > 0) {
      // return prev;
      // }

      const initial = {};

      const processDocs = (source, isTransfered) => {
        Object.entries(source || {}).forEach(([company, cases]) => {
          Object.entries(cases).forEach(([caseId, folders]) => {
            Object.entries(folders).forEach(([folderName, files]) => {
              const folderKey = `${caseId}-${folderName}${isTransfered ? "-transfered" : ""}`;

              if (!initial[folderKey]) {
                initial[folderKey] = [];
              }

              const sortedFiles = [...files].sort((a, b) => {
                const aHasOrder =
                  a.order_index !== null &&
                  a.order_index !== undefined &&
                  a.order_index !== "";
                const bHasOrder =
                  b.order_index !== null &&
                  b.order_index !== undefined &&
                  b.order_index !== "";

                const aIndex = Number(a.order_index);
                const bIndex = Number(b.order_index);

                // 1. SIN orden primero
                if (!aHasOrder && bHasOrder) return -1;
                if (aHasOrder && !bHasOrder) return 1;

                const getDate = (file) => {
                  let rawDate = file?.date_in;

                  if (!rawDate) return 0;

                  return new Date(
                    typeof rawDate === "string"
                      ? rawDate.replace(" ", "T")
                      : rawDate,
                  ).getTime();
                };

                // 2. Ninguno tiene orden → fecha DESC (con lógica matched/unmatched)
                if (!aHasOrder && !bHasOrder) {
                  const aDate = new Date(getDate(a));
                  const bDate = new Date(getDate(b));

                  return bDate - aDate;
                }

                // 3. Ambos tienen orden → order_index ASC
                return bIndex - aIndex;
              });

              initial[folderKey] = sortedFiles.map((f) => ({
                ...f,
                transfered: isTransfered,
                folder_name: folderName,
              }));
            });
          });
        });
      };

      processDocs(groupedDocsInfo, false);
      processDocs(groupedDocsInfoTransfered, true);

      return initial;
    });
  }, [groupedDocsInfo, groupedDocsInfoTransfered, docsChangedFlag]);

  return (
    <DndContext
      collisionDetection={pointerWithin}
      onDragStart={(event) => {
        const { active } = event;
        const [folder, path] = active.id.split("::");

        const file = orderedDocs[folder]?.find((f) => f.path === path);

        //  No permitir drag de transferidos
        if (file?.transfered) return;

        setActiveItem(file);
      }}
      onDragEnd={(event) => {
        handleDragEndGlobal(event);
        setActiveItem(null);
      }}
      onDragCancel={() => setActiveItem(null)}
    >
      <Box sx={{ width: "100%" }}>
        {/* TODO TU CONTENIDO NORMAL */}
        <FormControlLabel
          control={
            <Switch
              checked={allowCrossFolder}
              onChange={(e) => setAllowCrossFolder(e.target.checked)}
            />
          }
          label="Permitir mover archivos entre carpetas"
          sx={{ mb: 2 }}
        />
        <Alert severity="info" sx={{ mb: 2 }}>
          Solo tiene permitido eliminar información del caso actual.
        </Alert>
        <Alert severity="info" sx={{ mb: 2 }}>
          El sistema aplica un orden híbrido: los documentos identificados (con
          nombre) respetan el orden definido manualmente, en caso de no tener
          orden se subirá por orden de fecha, mientras que los documentos sin
          identificar se priorizan por fecha de carga, mostrando siempre los más
          recientes en primer lugar.
        </Alert>
        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveOrder}
            disabled={Object.keys(orderedDocs).length === 0}
          >
            Guardar orden
          </Button>
        </Box>
        <Box sx={{ width: "100%", mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Documentación del caso
          </Typography>
          {docsLoading ? (
            <Box
              sx={{
                width: "100%",
                minHeight: "280px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LoadingPage
                isLoading={true}
                heightProp="280px"
                widthProp="100%"
              />
            </Box>
          ) : !hasAnyDocs ? (
            Object.entries(groupedDocsInfo || {}).length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Cargando documentación... Si no aparece información, es posible
                que la documentación aún no exista o esté siendo procesada. Por
                favor, espere.
              </Alert>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  minHeight: "280px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LoadingPage
                  isLoading={true}
                  heightProp="280px"
                  widthProp="100%"
                />
              </Box>
            )
          ) : (
            renderDocumentSection(groupedDocsInfo, "", false, !!docsInfo)
          )}
        </Box>

        {!docsLoading &&
          hasTransferedDocs &&
          renderDocumentSection(
            groupedDocsInfoTransfered,
            "Documentos en otras empresas",
            true,
            !!docsInfoTransfered,
          )}
      </Box>

      <DragOverlay>
        {activeItem && !activeItem.transfered ? (
          <Paper
            sx={{
              p: 1,
              width: 300,
              display: "flex",
              alignItems: "center",
              gap: 1,
              boxShadow: 4,
              overflow: "hidden",
            }}
          >
            <InsertDriveFile color="info" />

            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {activeItem.file_name}
            </Typography>
          </Paper>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default ArchivosMostrar;
